// ============================================================
// StatusBadge.js
// Specialised Badge for domain-specific statuses. Keeps colour
// mapping in one place so a role 'admin' always renders purple,
// a lifecycle 'active' always renders green, etc.
// ============================================================

import React from 'react';
import Badge from './Badge';

// Platform-06 7-value client lifecycle vocabulary (ADR 91af669).
const LIFECYCLE_VARIANT = {
  trial_active:       'info',
  trial_expired:      'warning',
  trial_purged:       'neutral',
  client_active:      'success',
  client_terminating: 'warning',
  client_deactivated: 'neutral',
  client_purged:      'neutral',
};

const LIFECYCLE_LABEL = {
  trial_active:       'Trial',
  trial_expired:      'Trial expired',
  trial_purged:       'Trial purged',
  client_active:      'Active',
  client_terminating: 'Terminating',
  client_deactivated: 'Deactivated',
  client_purged:      'Purged',
};

const ROLE_VARIANT = {
  manager: 'teal',
  carer:   'blue',
  admin:   'purple',
  auditor: 'orange',
};

const ROLE_LABEL = {
  manager: 'Manager',
  carer:   'Carer',
  admin:   'Admin',
  auditor: 'Auditor',
};

const INVITATION_VARIANT = {
  pending:  'warning',
  redeemed: 'success',
  revoked:  'neutral',
  expired:  'danger',
};

const INVITATION_LABEL = {
  pending:  'Pending',
  redeemed: 'Redeemed',
  revoked:  'Revoked',
  expired:  'Expired',
};

export function LifecycleStatusBadge({ status }) {
  const variant = LIFECYCLE_VARIANT[status] || 'neutral';
  const label   = LIFECYCLE_LABEL[status]   || status || 'Unknown';
  return <Badge variant={variant}>{label}</Badge>;
}

export function RoleBadge({ roleKey }) {
  const variant = ROLE_VARIANT[roleKey] || 'neutral';
  const label   = ROLE_LABEL[roleKey]   || roleKey || 'Unknown';
  return <Badge variant={variant}>{label}</Badge>;
}

export function ActiveStatusBadge({ isActive }) {
  return (
    <Badge variant={isActive ? 'success' : 'neutral'}>
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

export function InvitationStatusBadge({ status }) {
  const variant = INVITATION_VARIANT[status] || 'neutral';
  const label   = INVITATION_LABEL[status]   || status || 'Unknown';
  return <Badge variant={variant}>{label}</Badge>;
}

// Federation state machine derived in src/utils/federationState.js.
// Priority: isActive-false > needsReconsent > consentGrantedAt-null > active.
const FEDERATION_VARIANT = {
  not_configured:   'neutral',
  deactivated:      'neutral',
  pending_consent:  'warning',
  active:           'success',
  needs_reconsent:  'danger',
};

const FEDERATION_LABEL = {
  not_configured:   'Not configured',
  deactivated:      'Deactivated',
  pending_consent:  'Awaiting consent',
  active:           'Federation active',
  needs_reconsent:  'Consent revoked',
};

export function FederationStatusBadge({ state }) {
  const variant = FEDERATION_VARIANT[state] || 'neutral';
  const label   = FEDERATION_LABEL[state]   || state || 'Unknown';
  return <Badge variant={variant}>{label}</Badge>;
}

// Tenant identity-provider — the canonical badge, used by the Tenants
// list AND the Tenant detail page (the list's inline ternary was
// retired 2026-09-03 because it labelled every non-M365 value
// "Google Workspace", which the new 'other' value would have worn).
// Vocabulary mirrors backend shared/tenant-identity-providers.js.
// consumer_email is AMBER on purpose: "no organisation tenant" is the
// MSP-conversation signal, and the list filter exists to surface it.
const IDENTITY_PROVIDER_VARIANT = {
  m365:             'info',
  google_workspace: 'teal',
  other:            'neutral',
  consumer_email:   'warning',
};

const IDENTITY_PROVIDER_LABEL = {
  m365:             'Microsoft 365',
  google_workspace: 'Google Workspace',
  other:            'Other business email / not yet known',
  consumer_email:   'Personal email, no organisation tenant',
};

export function IdentityProviderBadge({ provider }) {
  const variant = IDENTITY_PROVIDER_VARIANT[provider] || 'neutral';
  const label   = IDENTITY_PROVIDER_LABEL[provider]   || provider || 'Unknown';
  return <Badge variant={variant}>{label}</Badge>;
}

// Trial request status. Colours per brief: New=amber, Provisioned=teal,
// Extended=blue, Converted=green, Declined=grey.
const TRIAL_VARIANT = {
  New:         'warning',
  Provisioned: 'teal',
  Extended:    'blue',
  Converted:   'success',
  Declined:    'neutral',
};

export function TrialStatusBadge({ status }) {
  const variant = TRIAL_VARIANT[status] || 'neutral';
  return <Badge variant={variant}>{status || 'Unknown'}</Badge>;
}

// Setup-required — admin-role user with zero active ClientUserAgents
// grants. Returns null when false/undefined so callers can render it
// unconditionally inside a table cell without a wrapping ternary.
export function SetupRequiredBadge({ requiresAgentSetup }) {
  if (!requiresAgentSetup) return null;
  return <Badge variant="warning">Setup required</Badge>;
}

// Tenant search integration state (Phase 3 Stage 4 Phase 2a.3). State is
// DERIVED in TenantSearchTab.js (deriveIntegrationState) from the CTI row;
// 'pending_consent' is a component-local UI hint passed through here, not a
// DB status. degraded/expired append the consecutiveFailureCount.
const TENANT_INTEGRATION_VARIANT = {
  not_configured:  'neutral',
  pending_consent: 'info',
  active:          'success',
  degraded:        'warning',
  revoked:         'neutral',
  expired:         'danger',
};

export function TenantIntegrationStatusBadge({ state, consecutiveFailureCount }) {
  const variant = TENANT_INTEGRATION_VARIANT[state] || 'neutral';
  const n = typeof consecutiveFailureCount === 'number' ? consecutiveFailureCount : 0;
  let label;
  switch (state) {
    case 'not_configured':  label = 'Not configured'; break;
    case 'pending_consent': label = 'Pending consent'; break;
    case 'active':          label = 'Active'; break;
    case 'degraded':        label = `Degraded (${n} consecutive failure${n === 1 ? '' : 's'})`; break;
    case 'revoked':         label = 'Revoked'; break;
    case 'expired':         label = `Expired (${n} consecutive failure${n === 1 ? '' : 's'})`; break;
    default:                label = state || 'Unknown';
  }
  return <Badge variant={variant}>{label}</Badge>;
}
